// Router.js

export class Router {
    /**
         * @param {Object} options
         * @param {Application} options.app
         * @param {AuthService} options.authManager
         * @param {Array} options.routes
         * @param {function(routeMatch)|null} [options.onRouteChange]
         * @param {string|null} [options.defaultRouteName]   - preferovaná default routa
         * @param {string|null} [options.loginRouteName]     - routa pro login
         * @param {string|null} [options.forbiddenRouteName] - routa pro 403
         * @param {string|null} [options.notFoundRouteName]  - routa pro 404
         */
    constructor({
        app,
        authManager,
        routes,
        onRouteChange = null,
        defaultRouteName = null,
        loginRouteName = null,
        forbiddenRouteName = null,
        notFoundRouteName = null,
    } = {}) {

        if (!app) throw new Error('Router: "app" (Application) is required.');
        if (!authManager) throw new Error('Router: "authManager" (AuthService) is required.');
        if (!routes) throw new Error('Router: "routes" (array) is required.');

        this.app = app;
        this.authManager = authManager;
        this.onRouteChange = onRouteChange;

        this.defaultRouteName = defaultRouteName;
        this.loginRouteName = loginRouteName;
        this.forbiddenRouteName = forbiddenRouteName;
        this.notFoundRouteName = notFoundRouteName;

        this.routes = routes.map(r => this._compileRoute(r));
        this.activeRoute = null;

        this._onHashChange = this._onHashChange.bind(this);
    }

    start() {
        window.addEventListener('hashchange', this._onHashChange);

        // zkus zpracovat aktuální hash; když není validní → reset
        const ok = this._onHashChange();
        if (!ok) {
            this.routeToDefault();
        }
    }

    stop() {
        window.removeEventListener('hashchange', this._onHashChange);
    }

    getActiveRoute() { return this.activeRoute; }

    getDefaultRoute() {

        // 1) explicitně zadaná defaultní routa?
        if (this.defaultRouteName) {
            const route = this._findRoute(this.defaultRouteName);
            if (route && this._checkAccess(route.def).allowed)
                return route;
        }

        // 2) první dostupná routa označená jako default
        const defaultRoute = this._findFirstAccessibleDefaultRoute();
        if (defaultRoute)
            return defaultRoute;

        // 3) první dostupná routa
        const first = this._findFirstAccessibleRoute();
        if (first)
            return first;

        return null;

    }

    getRoutePath(name, params = {}, query = {}) {

        const route = this._findRoute(name);
        if (!route) {
            console.warn(`Route with name "${name}" not found.`);
            return null;
        }
        const path = this._buildPath(route, params);
        return this._attachQuery(path, query);

    }

    getRouteHash(name, params = {}, query = {}) {

        const url = this.getRoutePath(name, params, query);
        if (!url)
            return null;
        return '#' + url;

    }

    getRouteUrl(name, params = {}, query = {}) {

        const origin = window.location.origin;
        const pathname = window.location.pathname;
        const hash = this.getRouteHash(name, params, query);
        return origin + pathname + hash;

    }

    /**
     * Navigace podle name + params + query.
     */
    routeTo(routeName, params = {}, query = {}) {

        const hash = this.getRouteHash(routeName, params, query);
        if (!hash)
            return;
        this.navigateToHash(hash);

    }

    /**
     * Obecný reset aktivní routy:
     *  1) pokud je nastaven defaultRouteName → pokusí se na ni přejít
     *  2) jinak najde první dostupnou routu podle definic v routes.js
     */
    routeToDefault() {

        const defaultRoute = this.getDefaultRoute();
        if (defaultRoute) {
            this.routeTo(defaultRoute.def.name);
            return;
        }

        console.warn('Router.routeToDefault: no accessible routes found.');

    }

    navigateTo(url) { window.location.href = url; }

    navigateToHash(hash) { window.location.hash = hash; }

    navigateBack() { window.history.back(); }

    /**
     * Najde první routu s daným jménem.
     */
    _findRoute(name) {
        const route = this.routes.find(r => r.def.name === name) ?? null;
        return route;
    }

    /**
     * Najde první routu, na kterou má uživatel přístup (bere aktuální stav authManager).
     */
    _findFirstAccessibleRoute() {
        for (const r of this.routes) {
            const access = this._checkAccess(r.def);
            if (access.allowed) {
                return r;
            }
        }
        return null;
    }

    _findFirstAccessibleDefaultRoute() {
        for (const r of this.routes) {
            if (r?.def?.default !== true)
                continue;

            const access = this._checkAccess(r.def);
            if (access.allowed) {
                return r;
            }
        }

        return null;
    }

    // ----------------------------------------------------------
    // Interní část
    // ----------------------------------------------------------

    _onHashChange() {
        const raw = window.location.hash || '#';
        const pathWithQuery = raw.startsWith('#') ? raw.slice(1) : raw;

        const [path, queryString] = pathWithQuery.split('?', 2);
        const query = this._parseQuery(queryString || '');

        const match = this._matchPath(path);
        if (!match) {
            this._handleNotFound(path);
            return false;
        }

        const routeDef = match.def.def;

        const access = this._checkAccess(routeDef);
        if (!access.allowed) {
            this._handleAccessDenied(routeDef, access);
            return false;
        }

        const routeMatch = {
            def: routeDef,
            params: match.params,
            query,
            fullPath: path
        };

        this.activeRoute = routeMatch;

        if (this.onRouteChange) {
            this.onRouteChange(routeMatch);
        }

        return true;
    }

    _handleNotFound(path) {

        console.warn('Route not found for path:', path);
        if (this.notFoundRouteName && this._findRoute(this.notFoundRouteName)) {
            this.routeTo(this.notFoundRouteName);
            return;
        }
        // zkus najít nějakou použitelnou routu
        this.routeToDefault();

    }

    _handleAccessDenied(routeDef, access) {

        console.warn('Access denied:', routeDef.name, access.reason);

        if (access.reason === 'unauthenticated' && this.loginRouteName) {

            // přesměrování na login (s případným redirect parametrem)
            this.routeTo(this.loginRouteName, {}, { redirect: routeDef.path });

        } else if (access.reason === 'forbidden' && this.forbiddenRouteName) {

            this.routeTo(this.forbiddenRouteName);

        } else {

            // fallback – opět zkusíme nějakou jinou routu
            this.routeToDefault();

        }
    }

    _matchPath(path) {
        path = this._normalizePath(path);

        for (const r of this.routes) {
            const m = r.regex.exec(path);
            if (!m) continue;

            const params = {};
            r.params.forEach((p, idx) => {
                const rawVal = m[idx + 1];
                params[p.name] = this._convertParam(rawVal, p.type);
            });

            return { def: r, params };
        }
        return null;
    }

    _compileRoute(def) {
        const path = this._normalizePath(def.path);
        const colonParamRegex = /:([\w]+)/g;
        const braceParamRegex = /\{([\w]+)(?::([\w*]+))?\}/g;
        const params = [];
        let pattern = '^';
        let lastIndex = 0;

        const tokenRegex = new RegExp(`${colonParamRegex.source}|${braceParamRegex.source}`, 'g');

        let match;
        while ((match = tokenRegex.exec(path)) !== null) {
            const token = match[0];
            const name = match[1] || match[2];
            const type = match[3] || 'str';
            const staticPart = path.slice(lastIndex, match.index);

            pattern += this._escapeRegex(staticPart);
            params.push({ name, type });
            pattern += '(' + this._typeToRegex(type) + ')';
            lastIndex = match.index + token.length;
        }

        pattern += this._escapeRegex(path.slice(lastIndex));
        pattern += '$';

        const regex = new RegExp(pattern);

        return {
            def: Object.assign({}, def, { path }),
            regex,
            params
        };
    }

    _typeToRegex(type) {
        switch (type) {
            case 'num':
                return '\\d+';
            case 'slug':
                return '[a-zA-Z0-9-]+';
            case 'alpha':
                return '[a-zA-Z]+';
            case '*':
                return '.+';
            default:
                return '[^/]+';
        }
    }

    _convertParam(value, type) {
        if (type === 'num') {
            const n = Number(value);
            return Number.isNaN(n) ? null : n;
        }
        return value;
    }

    _escapeRegex(str) {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    _parseQuery(str) {
        if (!str) return {};
        const params = new URLSearchParams(str);
        const obj = {};
        for (const [k, v] of params.entries()) {
            if (obj.hasOwnProperty(k)) {
                if (!Array.isArray(obj[k])) {
                    obj[k] = [obj[k]];
                }
                obj[k].push(v);
            } else {
                obj[k] = v;
            }
        }
        return obj;
    }

    _attachQuery(path, query) {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) {
            if (v === undefined || v === null) continue;
            if (Array.isArray(v)) {
                v.forEach(x => params.append(k, String(x)));
            } else {
                params.append(k, String(v));
            }
        }
        const qs = params.toString();
        return qs ? `${path}?${qs}` : path;
    }

    _buildPath(compiledRoute, params) {
        const def = compiledRoute.def;
        let path = def.path;

        for (const p of compiledRoute.params) {
            const val = params[p.name];
            if (val === undefined || val === null) {
                throw new Error(`Missing param "${p.name}" for route "${def.name}".`);
            }
            path = path
                .replace(new RegExp(`:${p.name}\\b`), encodeURIComponent(String(val)))
                .replace(new RegExp(`\\{${p.name}(?::[\\w*]+)?\\}`), encodeURIComponent(String(val)));
        }

        return path;
    }

    _normalizePath(path) {
        const normalizedPath = path || '/';
        if (normalizedPath === '/')
            return normalizedPath;
        return normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
    }

    _checkAccess(routeDef) {
        // public route → vždy ok
        if (routeDef.public) {
            return { allowed: true, reason: 'public' };
        }

        // private → musí být přihlášený
        if (!this.authManager.isAuthenticated()) {
            return { allowed: false, reason: 'unauthenticated' };
        }

        if (!this.authManager.validateUserPrivileges(routeDef.privileges)) {
            return { allowed: false, reason: 'forbidden' };
        }

        return { allowed: true, reason: 'ok' };
    }

}
